import heroDemo from "../../assets/score-tracking-demo.webp";
import { APP_STORE_URL, GOOGLE_PLAY_URL } from "../../constants/urls";
import useReveal from "../../hooks/useReveal";
import GooglePlayIcon from "../icons/GooglePlayIcon";

export default function HeroSection({ isDark }) {
	const copyRef = useReveal(0);
	const mediaRef = useReveal(1);

	const surface = isDark ? "var(--color-night)" : "var(--color-paper)";
	const ink = isDark ? "var(--color-on-night)" : "var(--color-ink)";
	const muted = isDark ? "var(--color-on-night-muted)" : "var(--color-muted)";
	const rule = isDark ? "var(--color-night-rule)" : "var(--color-rule-2)";

	return (
		<section
			id="hero"
			className="pt-28 pb-20 md:pt-36 md:pb-28"
			style={{ background: surface }}
			aria-labelledby="hero-heading"
		>
			<div className="container mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
				<header ref={copyRef} className="reveal">
					<p
						className="font-display font-bold uppercase mb-4"
						style={{
							color: "var(--color-accent)",
							fontSize: "var(--text-sm)",
							letterSpacing: "var(--tracking-label)",
						}}
					>
						Apple Watch score tracker · Tennis · Padel · Pickleball
					</p>
					<h1
						id="hero-heading"
						className="font-display font-bold uppercase"
						style={{
							fontSize: "var(--text-display)",
							lineHeight: "var(--lh-tight)",
							letterSpacing: "var(--tracking-display)",
							color: ink,
							overflowWrap: "anywhere",
						}}
					>
						The score tracker that lives on your{" "}
						<span style={{ color: "var(--color-accent)" }}>wrist</span>
					</h1>
					<p
						className="mt-6"
						style={{
							fontSize: "var(--text-lg)",
							lineHeight: "var(--lh-body)",
							color: muted,
							maxWidth: "var(--measure)",
						}}
					>
						Keep score from your Apple Watch, then turn every tennis, padel,
						pickleball or badminton match into the stats that help you play
						better. No more “what was the score?”.
					</p>
					<div className="mt-8 flex flex-col sm:flex-row gap-4">
						<a
							href={APP_STORE_URL}
							target="_blank"
							rel="noopener noreferrer"
							className={`btn-slab lift ${isDark ? "btn-slab--accent" : "btn-slab--fill"}`}
							aria-label="Download Racketly on the App Store"
							title="Download Racketly - Multi-Sport Score Tracker for Apple Watch"
						>
							<svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
								<path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
							</svg>
							Download on App Store
						</a>
						<a
							href={GOOGLE_PLAY_URL}
							target="_blank"
							rel="noopener noreferrer"
							className={`btn-slab lift ${isDark ? "btn-slab--outline-night" : "btn-slab--outline"}`}
							aria-label="Get Racketly on Google Play"
						>
							<GooglePlayIcon />
							Get it on Google Play
						</a>
						<a
							href="#features"
							className={`btn-slab lift ${isDark ? "btn-slab--outline-night" : "btn-slab--outline"}`}
						>
							See how it works
						</a>
					</div>
					<p
						className="mt-8 pt-6"
						style={{
							borderTop: `var(--rule-hair) solid ${rule}`,
							fontSize: "var(--text-sm)",
							color: muted,
						}}
					>
						Available on iOS, Apple Watch & Android · Built for the court
					</p>
				</header>

				<aside
					ref={mediaRef}
					className="reveal relative justify-self-center lg:justify-self-end"
					aria-label="App demonstration"
				>
					<svg
						className="hero-court absolute pointer-events-none"
						style={{
							width: "150%",
							height: "150%",
							top: "50%",
							left: "50%",
							transform: "translate(-50%, -50%)",
							color: "var(--color-accent)",
							opacity: isDark ? 0.18 : 0.14,
						}}
						viewBox="0 0 200 320"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						aria-hidden="true"
					>
						<rect x="10" y="10" width="180" height="300" />
						<rect x="22" y="22" width="156" height="276" />
						<line x1="10" y1="160" x2="190" y2="160" strokeWidth="3" />
						<line x1="100" y1="10" x2="100" y2="310" opacity="0.5" />
					</svg>

					<figure
						className="relative max-w-[300px]"
						style={{ transform: "rotate(2deg)" }}
					>
						<div
							style={{
								border: `var(--rule-fine) solid ${isDark ? "var(--color-night-rule)" : "var(--color-ink)"}`,
								borderRadius: "var(--radius-card)",
								overflow: "hidden",
								background: isDark ? "var(--color-night-2)" : "var(--color-paper-2)",
							}}
						>
							<img
								src={heroDemo}
								alt="Racketly demonstration showing match scoring on Apple Watch"
								className="w-full aspect-[9/19.5] object-cover object-top"
								loading="eager"
								fetchpriority="high"
								width="352"
								height="764"
							/>
						</div>
					</figure>
				</aside>
			</div>
		</section>
	);
}
